import { Icon, StatusTag, Statuses } from '@whoop/web-components';
import Button from 'common/components/Button';
import DefaultPageLoader from 'common/components/DefaultPageLoader';
import Description from 'common/components/Description';
import Error from 'common/components/Error';
import LinkOut from 'common/components/LinkOut';
import Modal from 'common/components/Modal';
import PageHeader from 'common/components/PageHeader';
import Section from 'common/components/Section';
import AuthenticatedLayout from 'common/layouts/AuthenticatedLayout';
import apps from 'common/services/apps';
import developerRequests from 'common/services/developerRequests';
import { bandForCap } from 'common/tiers';
import { DeveloperRequestPayload } from 'common/types/developerRequests';
import config from 'config';
import React, { useState } from 'react';
import { useAsync, useAsyncCallback } from 'react-async-hook';
import { Link, useParams } from 'react-router-dom';
import RequestForm from '../../api-platform-requests/RequestForm';
import Scopes from '../common/Scopes';
import AppSecret from './AppSecret';
import styles from './index.module.scss';
import tierSummary from './tierSummary';

export function AppDetails() {
  const params = useParams();
  const getAppTask = useAsync(() => apps.getApp(params.id || 'asdf'), []);
  const app = getAppTask.result;

  const [requestOpen, setRequestOpen] = useState(false);

  const submitTask = useAsyncCallback(
    async (payload: DeveloperRequestPayload) => {
      await developerRequests.submitDeveloperRequest(payload);
    },
  );

  const submitError = submitTask.error
    ? submitTask.error.message ||
      'Something went wrong submitting your request. Please try again.'
    : undefined;

  const closeRequest = () => {
    setRequestOpen(false);
    submitTask.reset();
  };

  const limit = app?.adoptedUserLimit;
  const connected = app?.adoptedUserCount ?? 0;
  const currentBand = bandForCap(limit);

  // `!limit` would be wrong here: 0 is a real cap and null means no cap at all.
  const hasMemberLimit = limit != null;
  const connectedLabel = connected.toLocaleString();
  const chipDetail = hasMemberLimit
    ? `${connectedLabel} of ${limit.toLocaleString()} members`
    : `${connectedLabel} members`;

  return (
    <>
      {getAppTask.loading && <DefaultPageLoader />}
      {getAppTask.error && <Error>{getAppTask.error.message}</Error>}
      {app && (
        <div>
          <PageHeader
            title={app.name}
            actions={
              <div>
                <Link
                  to={`/apps/${app.clientId}/edit`}
                  data-testid="edit-app-button"
                >
                  <Button
                    variant="primary"
                    size="small"
                    theme="dark"
                    style={{
                      marginRight: 8,
                    }}
                  >
                    <Icon name="edit" />
                  </Button>
                </Link>
                <Link
                  to={`/apps/${app.clientId}/delete`}
                  data-testid="delete-app-button"
                >
                  <Button variant="normal" size="small" theme="dark">
                    <Icon name="trash" />
                  </Button>
                </Link>
              </div>
            }
          />

          {/* Deliberately unconditional: any tier can open a request. */}
          <Section
            title={
              currentBand ? (
                <>
                  Status&nbsp;&nbsp;
                  <StatusTag color={Statuses.INFO}>
                    {currentBand.name}
                    {` · ${chipDetail}`}
                  </StatusTag>
                </>
              ) : (
                'Requests'
              )
            }
            data-testid="app-approval-section"
          >
            <Description>
              <div data-testid="app-tier-summary">
                {tierSummary(limit, connected)}
              </div>
              <br />
              <Button
                variant="primary"
                theme="dark"
                data-testid="request-approval-button"
                onClick={() => setRequestOpen(true)}
              >
                Open Request
              </Button>
              <LinkOut href={config.appApprovalLearnMoreUrl}>
                <Button variant="link" theme="dark">
                  Learn More
                </Button>
              </LinkOut>
            </Description>
          </Section>
          <Section title="Credentials">
            <Description label="Client ID">
              <pre>{app.clientId}</pre>
            </Description>
            <Description label="Client Secret">
              <AppSecret secret={app.clientSecret} />
            </Description>
          </Section>

          <Section title="Details">
            <Description label="Logo">
              <img className={styles.logo} src={app.logoUrl} alt={app.name} />
            </Description>
            <Description label="Contacts">
              {app.contacts.join(', ') || '-'}
            </Description>
            <Description label="Privacy Policy">
              {app.privacyPolicyUrl || '-'}
            </Description>
            <Description label="Redirect URLs">
              {app.redirectUrls.join(', ') || '-'}
            </Description>
            <Description label="Scopes" />
            <Scopes scopes={app.scopes} />
          </Section>

          <Section title="Webhooks">
            <p>
              <LinkOut href={config.docs.webhooks}>Learn more</LinkOut> about
              webhooks with WHOOP.
            </p>
            {app.webhooks.length > 0 ? (
              app.webhooks.map((webhook, index) => (
                <Description key={webhook.id} label={`Webhook #${index + 1}`}>
                  <div key={webhook.id} className={styles.webhookDetailItem}>
                    <strong>URL:</strong> {webhook.url}
                    <br />
                    <strong>Version:</strong>{' '}
                    {webhook.model_version?.toUpperCase() || 'V1'}
                  </div>
                </Description>
              ))
            ) : (
              <Description label="Webhook URLs">
                No webhooks configured
              </Description>
            )}
          </Section>

          <Section title="Rate Limits">
            <Description label="API Day Rate Limit">
              {app.dayRequestLimit}
            </Description>
            <Description label="API Minute Rate Limit">
              {app.minuteRequestLimit}
            </Description>
          </Section>

          <Modal
            open={requestOpen}
            onClose={closeRequest}
            ariaLabel="WHOOP API Platform Requests"
            data-testid="request-modal"
          >
            <RequestForm
              appClientId={app.clientId}
              // No `appApproved` -- `!limit` conflated null with 0.
              currentUserLimit={app.adoptedUserLimit}
              currentUserCount={app.adoptedUserCount}
              currentMinuteLimit={app.minuteRequestLimit}
              currentDayLimit={app.dayRequestLimit}
              loading={submitTask.loading}
              error={submitError}
              submitted={submitTask.status === 'success'}
              onSubmit={submitTask.execute}
              onCancel={closeRequest}
              onBackFromStart={closeRequest}
            />
          </Modal>
        </div>
      )}
    </>
  );
}

export default () => (
  <AuthenticatedLayout pageIdentifier="app-details">
    <AppDetails />
  </AuthenticatedLayout>
);
